import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled from "styled-components";

import {Point3d, Vector3d} from "./util/Vector";

const GRID_MAX = 25;

const MainDeck = styled.div`
    margin: 0;    
    background-color: #dddddd;
`;

export class Holodeck extends Component {

    static propTypes = {
        dimension: PropTypes.number.isRequired,
        inclination: PropTypes.number.isRequired,
        view_angle: PropTypes.number.isRequired,
        field_depth: PropTypes.number.isRequired,
        field_span: PropTypes.number.isRequired,
        ctx: PropTypes.object.isRequired,
        canvas_ref: PropTypes.object.isRequired,
    }

    state = {
        grid_vectors: this.set_grid_vectors(),
        scene: this.add_grid(GRID_MAX, 1)
    }

    set_grid_vectors() {
        const h_grid_direction = new Point3d(
            Math.cos(this.props.view_angle + Math.PI / 2),
            Math.sin(this.props.view_angle + Math.PI / 2),
            0
        );
        const theta = this.props.view_angle;
        const phi = Math.PI / 2 - (this.props.inclination - Math.PI);
        const pov_direction = new Point3d(
            Math.sin(phi) * Math.cos(theta),
            Math.sin(phi) * Math.sin(theta),
            Math.cos(phi)
        );
        const null_point = Point3d.nullPoint();
        const pov_vector = Vector3d.scale(
            Vector3d.normalize(
                new Vector3d(null_point, pov_direction)
            ), this.props.field_depth
        );
        const h_grid_vector = Vector3d.normalize(new Vector3d(null_point, h_grid_direction));
        const v_grid_vector = Vector3d.normalize(Vector3d.crossProduct(h_grid_vector, pov_vector));
        return {
            pov_vector: pov_vector,
            v_grid_vector: v_grid_vector,
            h_grid_vector: h_grid_vector,
            pov_sum_squares:
                pov_vector.direction.x * pov_vector.direction.x +
                pov_vector.direction.y * pov_vector.direction.y +
                pov_vector.direction.z * pov_vector.direction.z,
            hy_by_hx: h_grid_vector.direction.y / h_grid_vector.direction.x
        }
    }

    componentDidMount() {
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.inclination !== this.props.inclination ||
            prevProps.view_angle !== this.props.view_angle ||
            prevProps.field_depth !== this.props.field_depth) {
            this.setState({grid_vectors: this.set_grid_vectors()});
        }
    }

    canvas_point(Q) {
        const grid_vectors = this.state.grid_vectors;
        const P = grid_vectors.pov_vector.direction;
        const sum_products = P.x * Q.x + P.y * Q.y + P.z * Q.z;
        const t = 1.0 / (1.0 - (grid_vectors.pov_sum_squares / sum_products));
        const x_t = (1.0 - t) * Q.x + t * P.x;
        const y_t = (1.0 - t) * Q.y + t * P.y;
        const V = grid_vectors.v_grid_vector.direction;
        const H = grid_vectors.h_grid_vector.direction;
        const B = (y_t - grid_vectors.hy_by_hx * x_t) / (V.y - grid_vectors.hy_by_hx * V.x);
        const A = (x_t - B * V.x) / H.x;
        const result = {h: A, v: B};
        return result;
    }

    compute_z(x, y) {
        const a = -16;
        const b = 15;
        const c = -27;
        const d = -18;
        const e = 17;
        const f = -16;
        const grid_x = x / GRID_MAX;
        const grid_y = y / GRID_MAX;
        const terms = [
            2 * Math.cos((a * x + c * y + f) / (a * Math.PI)),
            3 * Math.sin((b * x + d * y + e) / (b * Math.PI)),
            4 * grid_x * grid_x * grid_y,
            5 * grid_y * grid_y * grid_x,
        ];
        let sum = 0;
        for (let i = 0; i < terms.length; i++) {
            sum += terms[i];
        }
        return 0.75 * sum;
    }

    add_grid(grid_max, grid_width) {
        const scene = [];
        for (let x = grid_max * -1; x <= grid_max; x += grid_width) {
            const b = (((x + grid_max + 1) / (2 * grid_max)) * 250) % 255;
            for (let y = grid_max * -1; y <= grid_max; y += grid_width) {
                const g = (((y + grid_max + 1) / (2 * grid_max)) * 250) % 255;
                const r = ((b - g)) % 255;
                const z00 = this.compute_z(x, y)
                const z01 = this.compute_z(x, y + grid_width)
                const z10 = this.compute_z(x + grid_width, y)
                const z11 = this.compute_z(x + grid_width, y + grid_width)
                const fill_color = 'rgba(' + r + ',' + g + ',' + b + ', 0.35)';
                const line_color = 'rgba(' + r / 2 + ',' + g / 2 + ',' + b / 2 + ', 0.5)';
                scene.push({
                    fill_color: fill_color,
                    line_color: line_color,
                    data: [
                        {x: x, y: y, z: z00},
                        {x: x, y: y + grid_width, z: z01},
                        {x: x + grid_width, y: y + grid_width, z: z11},
                    ]
                });
                scene.push({
                    fill_color: fill_color,
                    line_color: line_color,
                    data: [
                        {x: x, y: y, z: z00},
                        {x: x + grid_width, y: y + grid_width, z: z11},
                        {x: x + grid_width, y: y, z: z10},
                    ]
                });
            }
        }
        return scene;
    }

    render_scene(ctx) {
        const scene = this.state.scene;
        const SCALE = this.props.field_span * 10;
        const OFFSET = this.props.dimension / 2;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, this.props.dimension, this.props.dimension);
        for (let elem = 0; elem < scene.length; elem++) {
            if (scene[elem].data.length < 2) {
                continue;
            }
            let first = this.canvas_point(scene[elem].data[0]);
            let region = new Path2D();
            region.moveTo(OFFSET + SCALE * first.h, OFFSET + SCALE * first.v);
            for (let d_index = 0; d_index < scene[elem].data.length - 1; d_index++) {
                const next = this.canvas_point(scene[elem].data[d_index + 1]);
                region.lineTo(OFFSET + SCALE * next.h, OFFSET + SCALE * next.v);
            }
            region.closePath();
            if (scene[elem].fill_color) {
                ctx.fillStyle = scene[elem].fill_color;
                ctx.fill(region);
            }
            if (scene[elem].line_color) {
                ctx.strokeStyle = scene[elem].line_color;
            } else {
                ctx.strokeStyle = "black";
            }
            ctx.stroke(region);
        }
    }

    render() {
        const deck_style = {
            width: this.props.dimension + "px",
            height: this.props.dimension + "px",
            border: this.props.dimension / 375 + "px solid #888888",
            borderRadius: this.props.dimension / 200 + "px"
        }
        console.log("rendering holodeck", this.props);
        console.log("this.state.grid_vectors", this.state.grid_vectors);
        if (this.props.ctx) {
            this.render_scene(this.props.ctx);
        }
        return <MainDeck style={deck_style}>
            <canvas
                ref={this.props.canvas_ref}
                width={this.props.dimension + 'px'}
                height={this.props.dimension + 'px'}
            />
        </MainDeck>
    }
}

export default Holodeck;